<template>
  <div class="donut-chart" :class="{ 'donut-chart--row': layout === 'row' }">
    <div class="donut-chart-svg-wrap" :style="{ width: size + 'px', height: size + 'px' }">
      <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`" role="img" :aria-label="ariaLabel">
        <circle
          v-if="total === 0"
          :cx="center" :cy="center" :r="radius"
          fill="none" stroke="#ECEFF1" :stroke-width="strokeWidth"
        />
        <circle
          v-for="(seg, i) in segments"
          :key="i"
          :cx="center" :cy="center" :r="radius"
          fill="none"
          :stroke="seg.color"
          :stroke-width="strokeWidth"
          :stroke-dasharray="`${seg.length} ${circumference - seg.length}`"
          :stroke-dashoffset="seg.offset"
          :transform="`rotate(-90 ${center} ${center})`"
          class="donut-segment"
        />
      </svg>
      <div class="donut-center">
        <span class="donut-total">{{ total }}</span>
        <span class="donut-total-label">{{ centerLabel }}</span>
      </div>
    </div>
    <ul class="donut-legend" v-if="showLegend">
      <li v-for="(seg, i) in segmentsWithPercent" :key="i" class="donut-legend-item">
        <span class="donut-legend-dot" :style="{ background: seg.color }"></span>
        <span class="donut-legend-label">{{ seg.label }}</span>
        <span class="donut-legend-value">{{ seg.value }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "DonutChart",
  props: {
    // data: [{ label, value, color }]
    data: { type: Array, default: () => [] },
    size: { type: Number, default: 160 },
    strokeWidth: { type: Number, default: 22 },
    centerLabel: { type: String, default: "total" },
    showLegend: { type: Boolean, default: true },
    layout: { type: String, default: "row" }, // 'row' | 'column'
  },
  computed: {
    center() {
      return this.size / 2;
    },
    radius() {
      return (this.size - this.strokeWidth) / 2;
    },
    circumference() {
      return 2 * Math.PI * this.radius;
    },
    total() {
      return this.data.reduce((sum, d) => sum + (d.value || 0), 0);
    },
    segments() {
      let cumulative = 0;
      return this.data
        .filter((d) => d.value > 0)
        .map((d) => {
          const fraction = d.value / this.total;
          const length = fraction * this.circumference;
          const offset = -cumulative * this.circumference;
          cumulative += fraction;
          return { color: d.color, length, offset };
        });
    },
    segmentsWithPercent() {
      return this.data.map((d) => ({
        ...d,
        percent: this.total > 0 ? Math.round((d.value / this.total) * 100) : 0,
      }));
    },
    ariaLabel() {
      return this.data.map((d) => `${d.label}: ${d.value}`).join(", ");
    },
  },
};
</script>

<style scoped>
.donut-chart {
  display: flex;
  align-items: center;
  gap: 24px;
}

.donut-chart--row {
  flex-direction: row;
}

.donut-chart-svg-wrap {
  position: relative;
  flex-shrink: 0;
}

.donut-segment {
  transition: stroke-dasharray 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.donut-center {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.donut-total {
  font-size: 1.75rem;
  font-weight: 700;
  color: #263238;
}

.donut-total-label {
  font-size: 0.7rem;
  color: #90A4AE;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.donut-legend {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  min-width: 0;
}

.donut-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 0.875rem;
}

.donut-legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
}

.donut-legend-label {
  color: #455A64;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.donut-legend-value {
  font-weight: 600;
  color: #263238;
}
</style>
